import { StyledDropdownWrap } from 'components/form/Textfield.styles'
import MultiLineField from '@mui/material/TextField'
import { Box, Flex } from 'components/elements'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from 'components/button'
import { styled } from '@stitches/react'
import { AxiosError } from 'axios'
import moment from 'moment'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { NotificationMessages, ValidationErrors, defaultImage } from 'utils/enum'
import { errorMessage } from 'utils/function'
import { useAddComment } from '../api/addComment'
import { useGetComments } from '../api/getComment'
import { Comment } from '../components/enums'
import { Comment_Type } from './enums'
import * as Yup from 'yup'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/Delete'
import useAuth from 'hooks/use-auth'
import { useDeleteComment } from '../api/deleteComment'
import { OnEdit } from './EditComment'
import { CommentFormFields, CommentsData } from './type'
import SnackBar from 'components/SnackBar'
import { Modal } from 'components/modal'

type CommentsProps = {
  row: Record<string, any>
  showMore: boolean
}

export const Comments = ({ row, showMore }: CommentsProps) => {
  const [notification, setNotification] = useState(false)
  const [editNotification, setEditNotification] = useState(false)
  const [commentDeleted, setCommentDeleted] = useState(false)
  const [editFlag, setEditFlag] = useState(false)
  const [deleteFlag, setDeleteFlag] = useState(false)
  const { user } = useAuth()
  const [submitError, setSubmitError] = useState('')
  const currentUserId = user?.id

  const [edit, setEdit] = useState(false)
  const [editID, setEditID] = useState(1)

  const { data: comments } = useGetComments({
    type: Comment_Type.type,
    userThreatId: row.threatId,
  })
  const { mutate, isLoading: isCommented } = useAddComment()
  const { mutate: deleteComment, isLoading: isDeleted } = useDeleteComment()
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<CommentFormFields>({
    resolver: yupResolver(commentSchema),
  })

  const onSubmit = (data: CommentFormFields) => {
    setSubmitError('')

    const newData = { ...data }
    newData[Comment.message] = String(data.message)
    newData[Comment.userThreatId] = row.threatId
    newData[Comment.type] = String('External')
    mutate(newData, {
      onError: error => {
        if (error instanceof AxiosError) {
          setSubmitError(errorMessage(error))
        }
      },
    })
    reset({ message: '' })
  }

  //delete comment
  const onDelete = (data: number) => {
    deleteComment(data, {
      onSuccess: () => {
        setSubmitError('')
        setDeleteFlag(false)
        setCommentDeleted(true)
        setNotification(true)
      },
      onError: error => {
        if (error instanceof AxiosError) {
          setSubmitError(errorMessage(error))
        }
      },
    })
  }

  return (
    <CommentBox>
      <Box as="h3" className="heading">
        Comments
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        {!!submitError && <ErrorBox>{submitError}</ErrorBox>}
        <StyledDropdownWrap>
          <MultiLineField
            multiline
            rows={3}
            type="text"
            style={{ width: '100%', height: '80%' }}
            className="main"
            {...register('message')}
            defaultValue={''}
            placeholder="Please add your comment here"
          />

          {errors.message && (
            <Box as="p" className="Error " css={{ color: '$red' }}>
              {errors?.message.message}
            </Box>
          )}
        </StyledDropdownWrap>

        <Box css={buttonCss}>
          <Button css={commentbuttonCss} disabled={isCommented} type="submit">
            {isCommented ? 'Sending...' : 'Send'}
          </Button>
        </Box>
      </form>

      <Box className={showMore ? 'full' : 'half'}>
        {comments?.data.comments.map((item: CommentsData, index: number) => {
          if (item.threatId === row.threatId)
            return (
              <Box className="comment-wrap" key={index}>
                <img alt="Comments" src={typeof item.imageUrl === 'string' ? item.imageUrl : item.imageUrl ?? defaultImage.admin} id="image2" className="user-img" />

                {edit === true && editID === item.id ? (
                  <OnEdit item={item} setEdit={setEdit} setEditFlag={setEditFlag} setEditNotification={setEditNotification} />
                ) : (
                  <Box className="comment-section">
                    <Flex direction="column">
                      <Box as="h4" className="name">
                        {item.userName}
                      </Box>
                      <Box as="p" className="comment">
                        • {item.message}
                      </Box>
                    </Flex>
                    <Flex className="comment-actions">
                      <Box as="label" className="date">
                        {moment(item.createdAt).fromNow()}
                      </Box>
                      {currentUserId === item.userId && (
                        <Box className="comment-icons">
                          <Box
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setEdit(true)
                              setEditID(item.id)
                            }}
                          >
                            <EditOutlinedIcon className="icon" />
                          </Box>
                          {/* delete comment */}
                          <Box style={{ cursor: 'pointer' }} onClick={() => setDeleteFlag(true)}>
                            <DeleteIcon className="icon" />
                          </Box>
                          <Modal
                            active={deleteFlag}
                            hideModal={() => setDeleteFlag(false)}
                            onAccept={() => onDelete(item.id)}
                            title="Delete Comment"
                            description={'Are you sure you want to delete comment'}
                            acceptText={'Delete'}
                            isLoading={isDeleted}
                          />
                        </Box>
                      )}
                    </Flex>
                  </Box>
                )}
              </Box>
            )
          return null
        })}
        <SnackBar
          open={notification && commentDeleted}
          onClose={() => {
            setNotification(false)
            setCommentDeleted(false)
          }}
          text={NotificationMessages.COMMENT_DELETE}
          severity={'success'}
        />
        <SnackBar
          open={editNotification && editFlag}
          onClose={() => {
            setEditNotification(false)

            setEditFlag(false)
          }}
          text={NotificationMessages.COMMENT_EDIT}
          severity={'success'}
        />
      </Box>
    </CommentBox>
  )
}

const commentSchema = Yup.object().shape({
  message: Yup.string().required(ValidationErrors.Message),
})

const ErrorBox = styled('div', {
  gridColumn: 'span 2',
  gridRow: 'span 3',
  margin: 0,
  color: '$error',
})

export const CommentBox = styled('div', {
  'width': '100%',
  'padding': '1.5rem 0 1rem',

  'form': {
    marginBottom: '1.5rem',
  },

  '&.multi-items': {
    'paddingTop': '1rem',

    '.comment-wrap': {
      paddingBottom: '1.75rem',
      borderBottom: '1px solid $gray3',
    },
  },

  '.MuiButtonBase-root': {
    padding: '.75rem 1.125rem',
  },

  '.comment-wrap': {
    'display': 'flex',
    'alignItems': 'flex-start',
    'paddingBottom': '1rem',
    'marginBottom': '.75rem',
    'borderBottom': '1px solid $gray3',

    'img': {
      width: '2.5rem',
      height: '2.5rem',
      objectFit: 'contain',
      marginBottom: 0,
      borderRadius: '50%',
      flexShrink: 0,
    },

    '&:last-child': {
      marginBottom: 0,
    },
  },

  '.comment-actions': {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: '1.5rem',
    marginLeft: '-3.5rem',
    width: 'calc(100% + 3.5rem)',
  },

  '.comment-icons': {
    display: 'flex',
    gap: '1rem',

    svg: {
      fontSize: '1rem',
    },
  },

  '.comment-section': {
    'width': '100%',
    'marginLeft': '1rem',

    '.name': {
      margin: 0,
    },

    '.comment': {
      margin: 0,
    },
  },
})

export const buttonCss = {
  marginTop: '1rem',
}

export const commentbuttonCss = {
  display: 'flex',
  alignItems: 'right',
  marginLeft: 'auto',
}

export default Comments
